import { useState } from 'react'
import { T } from '../../styles/theme'
import { SectionLabel } from '../../components/ui'
import BarcodeScanner from '../../components/BarcodeScanner'

function DoubleRing({ ingeridas, objetivo, size = 100 }) {
  const stroke1 = 7
  const stroke2 = 7
  const gap = 5
  const cx = size / 2
  const cy = size / 2
  const r1 = (size - stroke1) / 2
  const r2 = r1 - stroke1 / 2 - gap - stroke2 / 2
  const circ1 = 2 * Math.PI * r1
  const circ2 = 2 * Math.PI * r2

  const pct = objetivo > 0 ? ingeridas / objetivo : 0
  const clampedPct = Math.min(pct, 1.05)
  const color2 = T.blue

  return (
    <svg width={size} height={size}>
      {/* Fondo anillo exterior */}
      <circle cx={cx} cy={cy} r={r1} fill="none" stroke={T.cardB} strokeWidth={stroke1} />
      {/* Anillo exterior completo (gasto) */}
      <circle cx={cx} cy={cy} r={r1} fill="none" stroke={T.warn}
        strokeWidth={stroke1} opacity="0.55" strokeLinecap="round"
        strokeDasharray={`${circ1} 0`}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      {/* Fondo anillo interior */}
      <circle cx={cx} cy={cy} r={r2} fill="none" stroke={T.cardB} strokeWidth={stroke2} />
      {/* Anillo interior (ingeridas) */}
      {pct > 0 && (
        <circle cx={cx} cy={cy} r={r2} fill="none" stroke={color2}
          strokeWidth={stroke2} strokeLinecap="round"
          strokeDasharray={`${clampedPct * circ2} ${circ2}`}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dasharray 0.6s ease, stroke 0.3s ease' }}
        />
      )}
    </svg>
  )
}

const MEAL_LABELS = {
  desayuno: '🍳 Desayuno',
  comida: '🍛 Comida',
  cena: '🌙 Cena',
  snack: '🍎 Snack',
}

const inputFieldStyle = { width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }

export default function Hoy({ data }) {
  const { meals, goals, activityProfile, addMealItem, updateMealItem, deleteMealItem, addCustomFood, searchFoods, activity = [], addActivity } = data
  const [modal, setModal] = useState(false)
  const [addingTo, setAddingTo] = useState('cena')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [usdaResults, setUsdaResults] = useState([])
  const [selectedFood, setSelectedFood] = useState(null)
  const [qty, setQty] = useState(100)
  const [searching, setSearching] = useState(false)
  const [showUSDA, setShowUSDA] = useState(false)
  const [showCustomForm, setShowCustomForm] = useState(false)
  const [customFood, setCustomFood] = useState({ nombre: '', kcal_100g: '', proteina_100g: '', grasa_100g: '', carbohidratos_100g: '' })
  const [savingCustom, setSavingCustom] = useState(false)
  const [showScanner, setShowScanner] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [editQty, setEditQty] = useState(100)
  const [modalActivity, setModalActivity] = useState(false)
  const [activityForm, setActivityForm] = useState({
    tipo: '', duracion_min: '', kcal_estimadas: '', notas: ''
  })

  const allItems = meals.flatMap(m => m.meal_items ?? [])
  const totalKcal = allItems.reduce((s, i) => s + (i.foods.kcal_100g * i.cantidad_gramos / 100), 0)
  const totalProt = allItems.reduce((s, i) => s + (i.foods.proteina_100g * i.cantidad_gramos / 100), 0)
  const totalFat = allItems.reduce((s, i) => s + (i.foods.grasa_100g * i.cantidad_gramos / 100), 0)
  const totalCarb = allItems.reduce((s, i) => s + (i.foods.carbohidratos_100g * i.cantidad_gramos / 100), 0)

  const kcalObj = goals?.kcal_objetivo ?? 2000
  const protObj = goals?.proteina_objetivo ?? 150
  const fatObj = goals?.grasa_objetivo ?? 70
  const carbObj = goals?.carbohidratos_objetivo ?? 220
  const tdee = activityProfile?.tdee ?? null
  const balanceIsApprox = tdee == null
  const balance = Math.round(totalKcal - (tdee ?? kcalObj))

  async function handleSearch(q) {
    setQuery(q)
    setShowUSDA(false)
    setUsdaResults([])
    if (q.length < 2) { setResults([]); return }
    setSearching(true)
    const res = await searchFoods(q)
    const local = res.filter(f => f.fuente !== 'usda' || !f.id?.toString().startsWith('usda_'))
    setResults(local)
    setSearching(false)
  }

  async function handleSearchUSDA() {
    setSearching(true)
    const { searchUSDA } = await import('../../services/usdaSearch')
    const usda = await searchUSDA(query)
    setUsdaResults(usda)
    setShowUSDA(true)
    setSearching(false)
  }

  async function handleAdd() {
    if (!selectedFood) return
    let foodId = selectedFood.id
    if (typeof selectedFood.id === 'string' && selectedFood.id.startsWith('usda_')) {
      const { saveUSDAFood } = await import('../../services/usdaSearch')
      const { supabase } = await import('../../supabase')
      foodId = await saveUSDAFood(selectedFood, supabase)
    } else if (typeof selectedFood.id === 'string' && selectedFood.id.startsWith('barcode_')) {
      const { saveBarcodeFood } = await import('../../services/openFoodFacts')
      const { supabase } = await import('../../supabase')
      foodId = await saveBarcodeFood(selectedFood, supabase)
    }
    await addMealItem(addingTo, foodId, qty)
    setModal(false)
    setSelectedFood(null)
    setQuery('')
    setResults([])
    setUsdaResults([])
    setShowUSDA(false)
    setQty(100)
  }

  async function handleSaveCustomFood() {
    if (!customFood.nombre || !customFood.kcal_100g) return
    setSavingCustom(true)
    try {
      const food = await addCustomFood({
        nombre: customFood.nombre,
        kcal_100g: parseFloat(customFood.kcal_100g) || 0,
        proteina_100g: parseFloat(customFood.proteina_100g) || 0,
        grasa_100g: parseFloat(customFood.grasa_100g) || 0,
        carbohidratos_100g: parseFloat(customFood.carbohidratos_100g) || 0,
      })
      setShowCustomForm(false)
      setCustomFood({ nombre: '', kcal_100g: '', proteina_100g: '', grasa_100g: '', carbohidratos_100g: '' })
      setSelectedFood(food)
    } catch (e) {
      alert('No se pudo guardar el alimento: ' + e.message)
    }
    setSavingCustom(false)
  }

  function openEditItem(item) {
    setEditItem(item)
    setEditQty(item.cantidad_gramos)
  }

  async function handleSaveEditQty() {
    if (!editItem) return
    await updateMealItem(editItem.id, editQty)
    setEditItem(null)
  }

  async function handleDeleteItem() {
    if (!editItem) return
    await deleteMealItem(editItem.id)
    setEditItem(null)
  }

  const today = new Date().toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })

  return (
    <div>
      <div style={{ padding: '52px 20px 12px', position: 'sticky', top: 0, zIndex: 10, background: T.bg }}>
        <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>Hoy</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 2, textTransform: 'capitalize' }}>{today}</div>
      </div>

      {/* Resumen kcal */}
      <div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}>
            <DoubleRing ingeridas={totalKcal} objetivo={kcalObj} size={100} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Mono' }}>{Math.round(totalKcal)}</div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: 1, textTransform: 'uppercase' }}>kcal</div>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Mono', color: balance <= 0 ? T.accent : T.danger }}>
              {balance > 0 ? '+' : ''}{balance} kcal
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
              {balance <= 0 ? 'Déficit · vas bien 💪' : 'Superávit · revisa tu ingesta'}
              {balanceIsApprox && ' (aprox.)'}
            </div>
            {balanceIsApprox && (
              <div style={{ fontSize: 10, color: T.warn, marginTop: 2 }}>
                Tu profesional aún no ha calculado tu gasto energético
              </div>
            )}
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: T.muted }}>🔥 Ingeridas</span>
                <span style={{ fontFamily: 'DM Mono', fontWeight: 600, color: T.blue }}>{Math.round(totalKcal)} kcal</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: T.muted }}>🎯 Objetivo ingesta</span>
                <span style={{ fontFamily: 'DM Mono', fontWeight: 600, color: T.text }}>{kcalObj} kcal</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: T.muted }}>⚡ Gasto total estimado</span>
                <span style={{ fontFamily: 'DM Mono', fontWeight: 600, color: T.warn }}>{tdee != null ? `${Math.round(tdee)} kcal` : 'Pendiente'}</span>
              </div>
              <div style={{ height: 1, background: T.cardB, margin: '2px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: T.muted }}>⚖️ Balance{balanceIsApprox ? ' (aprox.)' : ''}</span>
                <span style={{ fontFamily: 'DM Mono', fontWeight: 700, color: balance <= 0 ? T.accent : T.danger }}>
                  {balance > 0 ? '+' : ''}{balance} kcal
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Leyenda dinámica */}
        <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.cardB}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 4, borderRadius: 2, background: T.warn, opacity: 0.55 }} />
            <span style={{ fontSize: 11, color: T.muted }}>Objetivo ({kcalObj} kcal)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 4, borderRadius: 2, background: T.blue }} />
            <span style={{ fontSize: 11, color: T.muted }}>Ingeridas</span>
          </div>
        </div>
      </div>

      {/* Macros */}
      <div style={{ display: 'flex', gap: 8, margin: '0 16px 12px' }}>
        {[
          ['Proteína', totalProt, protObj, 'g', T.blue],
          ['Grasas', totalFat, fatObj, 'g', T.warn],
          ['Carbos', totalCarb, carbObj, 'g', T.purple],
        ].map(([l, v, o, u, c]) => (
          <div key={l} style={{ flex: 1, background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Mono', color: c }}>
              {Math.round(v)}<span style={{ fontSize: 10, fontFamily: 'DM Sans' }}>{u}</span>
            </div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{l}</div>
            <div style={{ height: 3, background: T.cardB, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: c, width: `${Math.min(v / o * 100, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Comidas */}
      <SectionLabel>
        Comidas del día
      </SectionLabel>
      {Object.entries(MEAL_LABELS).map(([tipo, label]) => {
        const meal = meals.find(m => m.tipo === tipo)
        const items = meal?.meal_items ?? []
        const mealKcal = items.reduce((s, i) => s + (i.foods.kcal_100g * i.cantidad_gramos / 100), 0)
        return (
          <div key={tipo} style={{ margin: '0 16px 10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.sub }}>{label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {items.length > 0 && <div style={{ fontSize: 12, color: T.muted, fontFamily: 'DM Mono' }}>{Math.round(mealKcal)} kcal</div>}
                <button onClick={() => { setAddingTo(tipo); setModal(true) }} style={{ background: 'none', border: 'none', color: T.accent, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>+</button>
              </div>
            </div>
            {items.length === 0
              ? <div onClick={() => { setAddingTo(tipo); setModal(true) }} style={{ background: T.card, border: `1px dashed ${T.cardB}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center', color: T.muted, fontSize: 13, cursor: 'pointer' }}>
                  Toca + para añadir
                </div>
              : items.map((item, i) => {
                  const kcal = Math.round(item.foods.kcal_100g * item.cantidad_gramos / 100)
                  const prot = (item.foods.proteina_100g * item.cantidad_gramos / 100).toFixed(1)
                  return (
                    <div key={i} onClick={() => openEditItem(item)} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 10, padding: '10px 12px', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{item.foods.nombre} ({item.cantidad_gramos}g)</div>
                        <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>P: {prot}g · toca para editar</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'DM Mono', color: T.accent }}>{kcal}</div>
                    </div>
                  )
                })}
          </div>
        )
      })}

      {/* Actividad física */}
      <SectionLabel style={{ margin: '16px 20px 8px' }}>
        Actividad física hoy
      </SectionLabel>
      <div style={{ margin: '0 16px 12px' }}>
        {activity.length === 0 ? (
          <div onClick={() => setModalActivity(true)} style={{ background: T.card, border: `1px dashed ${T.cardB}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center', color: T.muted, fontSize: 13, cursor: 'pointer' }}>
            Toca + para añadir actividad
          </div>
        ) : (
          <>
            {activity.map((a, i) => (
              <div key={i} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 10, padding: '10px 12px', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>🏃 {a.tipo}</div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>
                    {a.duracion_min ? `${a.duracion_min} min` : ''}{a.notas ? ` · ${a.notas}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'DM Mono', color: T.warn }}>
                  -{a.kcal_estimadas} kcal
                </div>
              </div>
            ))}
            <button onClick={() => setModalActivity(true)} style={{ width: '100%', marginTop: 6, padding: '10px 0', borderRadius: 10, background: 'transparent', border: `1px solid ${T.cardB}`, color: T.muted, fontSize: 13, cursor: 'pointer' }}>
              + Añadir otra actividad
            </button>
          </>
        )}
      </div>

      {/* FAB */}
      <button onClick={() => { setAddingTo('snack'); setModal(true) }} style={{
        position: 'fixed', bottom: 92, right: 20, width: 52, height: 52,
        borderRadius: 16, background: T.accent, color: '#000', fontSize: 26,
        border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', boxShadow: '0 4px 24px rgba(74,222,128,.35)', zIndex: 20
      }}>+</button>

      {/* MODAL AÑADIR COMIDA */}
      {modal && (
        <div onClick={e => { if (e.target === e.currentTarget) { setModal(false); setSelectedFood(null); setQuery(''); setResults([]); setUsdaResults([]); setShowUSDA(false); setShowCustomForm(false); } }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>
              {!selectedFood ? `Añadir a ${addingTo}` : selectedFood.nombre}
            </div>
            {showCustomForm ? <>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 12, lineHeight: 1.4 }}>
                Añade tú los datos del alimento (por cada 100g). Se guardará para que puedas volver a usarlo.
              </div>
              <div style={{ marginBottom: 10 }}>
                <input placeholder="Nombre del alimento" value={customFood.nombre}
                  onChange={e => setCustomFood(f => ({ ...f, nombre: e.target.value }))}
                  style={inputFieldStyle}
                />
              </div>
              <div style={{ marginBottom: 10 }}>
                <input type="number" placeholder="Kcal por 100g" value={customFood.kcal_100g}
                  onChange={e => setCustomFood(f => ({ ...f, kcal_100g: e.target.value }))}
                  style={inputFieldStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input type="number" placeholder="Proteína g" value={customFood.proteina_100g}
                  onChange={e => setCustomFood(f => ({ ...f, proteina_100g: e.target.value }))}
                  style={inputFieldStyle}
                />
                <input type="number" placeholder="Grasas g" value={customFood.grasa_100g}
                  onChange={e => setCustomFood(f => ({ ...f, grasa_100g: e.target.value }))}
                  style={inputFieldStyle}
                />
                <input type="number" placeholder="Carbos g" value={customFood.carbohidratos_100g}
                  onChange={e => setCustomFood(f => ({ ...f, carbohidratos_100g: e.target.value }))}
                  style={inputFieldStyle}
                />
              </div>
              <button onClick={handleSaveCustomFood} disabled={savingCustom || !customFood.nombre || !customFood.kcal_100g} style={{
                width: '100%', padding: 14, borderRadius: 14,
                background: (customFood.nombre && customFood.kcal_100g) ? T.accent : T.cardB,
                color: (customFood.nombre && customFood.kcal_100g) ? '#000' : T.muted,
                fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer'
              }}>
                {savingCustom ? 'Guardando...' : 'Guardar y usar'}
              </button>
              <button onClick={() => setShowCustomForm(false)} style={{ width: '100%', padding: 14, borderRadius: 14, background: 'transparent', color: T.text, fontSize: 15, fontWeight: 600, border: `1px solid ${T.cardB}`, cursor: 'pointer', marginTop: 8 }}>
                ← Volver a la búsqueda
              </button>
            </> : !selectedFood ? <>
              <button onClick={() => setShowScanner(true)} style={{
                width: '100%', padding: 12, borderRadius: 12, marginBottom: 10,
                background: T.accentSoft, border: `1px solid ${T.accent}`, color: T.accent,
                fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans'
              }}>
                📷 Escanear código de barras
              </button>
              <input autoFocus placeholder="Buscar alimento..." value={query}
                onChange={e => handleSearch(e.target.value)}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
              />
              <div style={{ marginTop: 8 }}>
                {searching && <div style={{ textAlign: 'center', color: T.muted, padding: 20 }}>Buscando...</div>}
                {!searching && results.length === 0 && query.length >= 2 && !showUSDA && (
                  <div style={{ textAlign: 'center', color: T.muted, padding: 20, fontSize: 13 }}>No encontrado en la base española.</div>
                )}
                {!searching && results.map(f => (
                  <div key={f.id} onClick={() => setSelectedFood(f)} style={{ padding: '12px 0', borderBottom: `1px solid ${T.cardB}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{f.nombre}</div>
                      <div style={{ fontSize: 12, color: T.muted }}>P:{f.proteina_100g}g G:{f.grasa_100g}g C:{f.carbohidratos_100g}g (100g)</div>
                    </div>
                    <div style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 600, color: T.accent }}>{f.kcal_100g}</div>
                  </div>
                ))}
                {!searching && query.length >= 2 && !showUSDA && (
                  <button onClick={handleSearchUSDA} style={{ width: '100%', marginTop: 12, padding: '11px 0', borderRadius: 12, background: 'transparent', border: `1px solid ${T.cardB}`, color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans' }}>
                    🔍 Buscar más en base internacional
                  </button>
                )}
                {showUSDA && !searching && <>
                  <SectionLabel style={{ margin: '16px 0 8px' }}>Base internacional (USDA)</SectionLabel>
                  {usdaResults.length === 0 && <div style={{ textAlign: 'center', color: T.muted, padding: 20, fontSize: 13 }}>Sin resultados en USDA tampoco</div>}
                  {usdaResults.map(f => (
                    <div key={f.id} onClick={() => setSelectedFood(f)} style={{ padding: '12px 0', borderBottom: `1px solid ${T.cardB}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{f.nombre}</div>
                        <div style={{ fontSize: 12, color: T.muted }}>P:{f.proteina_100g}g G:{f.grasa_100g}g C:{f.carbohidratos_100g}g (100g)</div>
                      </div>
                      <div style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 600, color: T.accent }}>{f.kcal_100g}</div>
                    </div>
                  ))}
                  <button onClick={() => { setShowUSDA(false); setUsdaResults([]) }} style={{ width: '100%', marginTop: 12, padding: '11px 0', borderRadius: 12, background: 'transparent', border: `1px solid ${T.cardB}`, color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans' }}>
                    ← Volver a resultados españoles
                  </button>
                </>}
                {!searching && (
                  <button onClick={() => setShowCustomForm(true)} style={{ width: '100%', marginTop: 12, padding: '11px 0', borderRadius: 12, background: 'transparent', border: `1px dashed ${T.cardB}`, color: T.accent, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans' }}>
                    ➕ No lo encuentro, añadirlo yo mismo
                  </button>
                )}
              </div>
            </> : <>
              <div style={{ background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: 12, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: T.sub }}>Por {qty}g</span>
                <span style={{ fontFamily: 'DM Mono', fontSize: 14, fontWeight: 700, color: T.accent }}>{Math.round(selectedFood.kcal_100g * qty / 100)} kcal</span>
              </div>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 8, textAlign: 'center' }}>Cantidad (gramos) · toca el número para escribirla</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                <button onClick={() => setQty(q => Math.max(1, q - 10))} style={{ width: 40, height: 40, borderRadius: 10, background: T.bg, border: `1px solid ${T.cardB}`, color: T.text, fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>−</button>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <input type="number" value={qty} onChange={e => setQty(parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', textAlign: 'center', fontSize: 28, fontWeight: 700, fontFamily: 'DM Mono', background: 'transparent', border: 'none', color: T.text, outline: 'none' }}
                  />
                  <span style={{ fontSize: 14, color: T.muted }}>gramos</span>
                </div>
                <button onClick={() => setQty(q => q + 10)} style={{ width: 40, height: 40, borderRadius: 10, background: T.bg, border: `1px solid ${T.cardB}`, color: T.text, fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>+</button>
              </div>
              <button onClick={handleAdd} style={{ width: '100%', padding: 14, borderRadius: 14, background: T.accent, color: '#000', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                Añadir al registro
              </button>
              <button onClick={() => { setSelectedFood(null); setQuery(''); setResults([]); setUsdaResults([]); setShowUSDA(false); }} style={{ width: '100%', padding: 14, borderRadius: 14, background: 'transparent', color: T.text, fontSize: 15, fontWeight: 600, border: `1px solid ${T.cardB}`, cursor: 'pointer', marginTop: 8 }}>
                ← Cambiar alimento
              </button>
            </>}
          </div>
        </div>
      )}

      {/* MODAL ACTIVIDAD */}
      {modalActivity && (
        <div onClick={e => { if (e.target === e.currentTarget) setModalActivity(false) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Registrar actividad</div>

            <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>Actividad rápida</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {[['Caminar', 280], ['Correr', 600], ['Bicicleta', 450], ['Natación', 500], ['CrossFit', 550], ['Pesas', 350], ['Yoga', 200], ['Pilates', 250], ['Escalada', 500]].map(([tipo, kcalH]) => (
                <button key={tipo} onClick={() => setActivityForm(f => ({ ...f, tipo }))} style={{
                  padding: '8px 14px', borderRadius: 20,
                  border: `1px solid ${activityForm.tipo === tipo ? T.accent : T.cardB}`,
                  background: activityForm.tipo === tipo ? T.accentSoft : T.bg,
                  color: activityForm.tipo === tipo ? T.accent : T.muted,
                  fontSize: 13, cursor: 'pointer'
                }}>{tipo}</button>
              ))}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>O escribe el tipo</div>
              <input placeholder="ej: Fútbol, Tenis..." value={activityForm.tipo}
                onChange={e => setActivityForm(f => ({ ...f, tipo: e.target.value }))}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', fontSize: 15, fontFamily: 'DM Sans', outline: 'none', color: T.text }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Duración (minutos)</div>
              <input type="number" placeholder="ej: 45" value={activityForm.duracion_min}
                onChange={e => {
                  const min = parseFloat(e.target.value)
                  const kcalPorHora = { 'Caminar': 280, 'Correr': 600, 'Bicicleta': 450, 'Natación': 500, 'CrossFit': 550, 'Pesas': 350, 'Yoga': 200, 'Pilates': 250, 'Escalada': 500 }
                  const kcalH = kcalPorHora[activityForm.tipo] || 400
                  const kcal = min ? Math.round(kcalH * min / 60) : ''
                  setActivityForm(f => ({ ...f, duracion_min: e.target.value, kcal_estimadas: kcal }))
                }}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', fontSize: 15, fontFamily: 'DM Sans', outline: 'none', color: T.text }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Calorías quemadas (estimadas)</div>
              <input type="number" placeholder="Se calcula automáticamente" value={activityForm.kcal_estimadas}
                onChange={e => setActivityForm(f => ({ ...f, kcal_estimadas: e.target.value }))}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', fontSize: 15, fontFamily: 'DM Sans', outline: 'none', color: T.text }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Notas (opcional)</div>
              <input placeholder="ej: Carrera suave por el parque" value={activityForm.notas}
                onChange={e => setActivityForm(f => ({ ...f, notas: e.target.value }))}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', fontSize: 15, fontFamily: 'DM Sans', outline: 'none', color: T.text }}
              />
            </div>

            <button onClick={async () => {
              if (!activityForm.tipo) return
              await addActivity({
                tipo: activityForm.tipo,
                duracion_min: parseFloat(activityForm.duracion_min) || null,
                kcal_estimadas: parseFloat(activityForm.kcal_estimadas) || null,
                notas: activityForm.notas || null,
              })
              setModalActivity(false)
              setActivityForm({ tipo: '', duracion_min: '', kcal_estimadas: '', notas: '' })
            }} style={{
              width: '100%', padding: 14, borderRadius: 14,
              background: activityForm.tipo ? T.accent : T.cardB,
              color: activityForm.tipo ? '#000' : T.muted,
              fontSize: 15, fontWeight: 700, border: 'none',
              cursor: activityForm.tipo ? 'pointer' : 'not-allowed'
            }}>
              Guardar actividad
            </button>
          </div>
        </div>
      )}

      {/* MODAL EDITAR ALIMENTO YA AÑADIDO */}
      {editItem && (
        <div onClick={e => { if (e.target === e.currentTarget) setEditItem(null) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%' }}>
            <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{editItem.foods.nombre}</div>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 8, textAlign: 'center' }}>Cantidad (gramos) · toca el número para escribirla</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
              <button onClick={() => setEditQty(q => Math.max(1, q - 10))} style={{ width: 40, height: 40, borderRadius: 10, background: T.bg, border: `1px solid ${T.cardB}`, color: T.text, fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>−</button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <input type="number" value={editQty} onChange={e => setEditQty(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', textAlign: 'center', fontSize: 28, fontWeight: 700, fontFamily: 'DM Mono', background: 'transparent', border: 'none', color: T.text, outline: 'none' }}
                />
                <span style={{ fontSize: 14, color: T.muted }}>gramos</span>
              </div>
              <button onClick={() => setEditQty(q => q + 10)} style={{ width: 40, height: 40, borderRadius: 10, background: T.bg, border: `1px solid ${T.cardB}`, color: T.text, fontSize: 20, cursor: 'pointer', flexShrink: 0 }}>+</button>
            </div>
            <button onClick={handleSaveEditQty} style={{ width: '100%', padding: 14, borderRadius: 14, background: T.accent, color: '#000', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              Guardar cambios
            </button>
            <button onClick={handleDeleteItem} style={{ width: '100%', padding: 14, borderRadius: 14, background: 'transparent', color: T.danger, fontSize: 15, fontWeight: 600, border: `1px solid ${T.danger}`, cursor: 'pointer', marginTop: 8 }}>
              🗑️ Eliminar alimento
            </button>
            <button onClick={() => setEditItem(null)} style={{ width: '100%', padding: 14, borderRadius: 14, background: 'transparent', color: T.text, fontSize: 15, fontWeight: 600, border: `1px solid ${T.cardB}`, cursor: 'pointer', marginTop: 8 }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ESCÁNER DE CÓDIGO DE BARRAS */}
      {showScanner && (
        <BarcodeScanner
          onClose={() => setShowScanner(false)}
          onDetected={(food) => {
            setShowScanner(false)
            setSelectedFood(food)
            setQty(100)
          }}
        />
      )}
    </div>
  )
}